// Congés annuels — leave-balances screen (soft-island port of the
// design/Congés.html prototype, cg-* files). Replaces the old
// Balances page at /balances; Récupération now lives on its own
// page (/recup).
//
// Deviations from the prototype (deliberate):
// - the proto's own topbar/page links/entity pills/theme toggle/Tweaks
//   panel are dropped — the app shell + EntityBar own those,
// - absence types are limited to what the ledger supports (congé
//   annuel); maladie/sans-solde days live in Saisie quotidienne,
// - the team calendar is fed by /monthly-grid, so it is per-entity
//   (the "Toutes" filter shows a picker hint, like Grille mensuelle),
// - "demandes" cards cover the real Lamis→Bouchra CONGE workflow.
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useUiStore } from '../store/ui.js';
import {
  useLeaveBalances, useLeavesOverview, usePendingRequests,
  type LeaveBalanceRow, type MonthCellType,
} from '../api/hooks.js';
import {
  CgCount, EmpCell, balanceByEmp, cgNum, cgToday, matchesQuery, shiftYm, ymLabel,
} from '../components/conges/common.js';
import { LeaveCalendar } from '../components/conges/LeaveCalendar.js';
import { DemandesView } from '../components/conges/DemandesView.js';
import { OverviewView } from '../components/conges/OverviewView.js';
import { EmployeeDrawer } from '../components/conges/EmployeeDrawer.js';
import { SaisieModal, type SaisiePreset } from '../components/conges/SaisieModal.js';
import { CreditRecupModal } from '../components/conges/CreditRecupModal.js';
import '../styles/conges.css';

// « apercu » est l'accueil: résumé du jour + ce qui attend + ce qui
// cloche. Les trois autres onglets restent tels quels.
type View = 'apercu' | 'soldes' | 'calendrier' | 'demandes';

// Le calendrier est un calendrier de CONGÉS: par défaut il montre les
// congés et les récups — les absences (maladie, justifiées, non
// justifiées) vivent dans Saisie quotidienne et Inspection. Elles
// restent accessibles derrière un interrupteur, parce qu'elles comptent
// pour juger la couverture d'une journée.
const CAL_LEAVE_TYPES: MonthCellType[] = ['c', 'recup'];
const CAL_ABSENCE_TYPES: MonthCellType[] = ['m', 'aj', 'anj'];

export function CongesPage() {
  const themeDark = useUiStore((s) => s.theme) === 'dark';
  const entityId = useUiStore((s) => s.currentEntityId);
  const [searchParams, setSearchParams] = useSearchParams();
  const view: View = (['apercu', 'soldes', 'calendrier', 'demandes'] as const).find((v) => v === searchParams.get('view')) ?? 'apercu';
  const month = /^\d{4}-\d{2}$/.test(searchParams.get('month') ?? '') ? searchParams.get('month')! : cgToday().slice(0, 7);

  const [q, setQ] = useState('');
  const [drawerEmp, setDrawerEmp] = useState<string | null>(null);
  const [saisie, setSaisie] = useState<SaisiePreset | null>(null);
  const [credit, setCredit] = useState<{ empId?: string | null } | null>(null);
  const [showAbsences, setShowAbsences] = useState(false);

  const overview = useLeavesOverview({ entityId });
  const conges = useLeaveBalances({ type: 'CONGE', entityId });
  const recups = useLeaveBalances({ type: 'RECUP', entityId });
  const pending = usePendingRequests({ entityId });

  const setParams = (patch: Record<string, string | null>) => {
    const p = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null) p.delete(k);
      else p.set(k, v);
    }
    setSearchParams(p, { replace: true });
  };
  const go = (v: View) => setParams({ view: v === 'apercu' ? null : v });

  const rows = useMemo(
    () => (conges.data ?? []).filter((r) => matchesQuery(r.employee, q)),
    [conges.data, q],
  );
  const recupMap = useMemo(() => balanceByEmp(recups.data), [recups.data]);
  const balMap = useMemo(() => balanceByEmp(conges.data), [conges.data]);
  const pendingByEmp = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of pending.data ?? []) map.set(r.employee.id, (map.get(r.employee.id) ?? 0) + r.daysCount);
    return map;
  }, [pending.data]);
  const calBalMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of conges.data ?? []) map.set(r.employee.id, r.balance.current);
    return map;
  }, [conges.data]);

  const K = rows.reduce(
    (a, r) => ({
      solde: a.solde + r.balance.current,
      acquis: a.acquis + r.balance.accrued,
      consomme: a.consomme + r.balance.consumed,
      low: a.low + (r.balance.current < 5 ? 1 : 0),
    }),
    { solde: 0, acquis: 0, consomme: 0, low: 0 },
  );
  const nPending = pending.data?.length ?? 0;
  const year = new Date().getFullYear();
  const nOnLeave = overview.data?.onLeave.length ?? 0;
  const nRegul = overview.data?.toRegularize.length ?? 0;

  const KPIS: Array<{ v: number; dec?: number; s?: string; cls?: string; l: string; go?: View }> = [
    { v: K.solde, cls: 'accent', s: 'j', l: `Solde congés · ${rows.length} employés` },
    { v: nOnLeave, dec: 0, cls: nOnLeave ? 'accent' : '', l: "En congé aujourd'hui", go: 'apercu' },
    { v: K.consomme, s: 'j', l: `Consommé en ${year}` },
    { v: K.low, dec: 0, cls: K.low ? 'warn' : '', l: 'Soldes sous 5 j' },
    { v: nPending, dec: 0, cls: nPending ? 'warn' : '', l: 'Demandes en attente', go: 'demandes' },
  ];

  return (
    <div className={'cgapp' + (themeDark ? ' theme-dark' : '')}>
      <div className="cg-scrollpage">
        <div className="gm-wrap">
          <div className="gm-eyebrow">Données · Congés annuels</div>
          <h1 className="gm-title">Congés <em>annuels</em></h1>
          <p className="gm-sub">
            Soldes par employé, acquisition automatique de 1,83 j par mois, planning d'équipe et validation
            des demandes — chaque mouvement est tracé dans le journal.
          </p>

          <div className="cg-ctrl">
            <div className="cg-seg">
              {([['apercu', "Vue d'ensemble"], ['soldes', 'Soldes équipe'], ['calendrier', 'Calendrier'], ['demandes', 'Demandes']] as const).map(([k, l]) => (
                <button key={k} className={view === k ? 'on' : ''} onClick={() => go(k)} data-testid={`cg-tab-${k}`}>
                  {l}
                  {k === 'demandes' && nPending > 0 && <span className="bdg">{nPending}</span>}
                  {k === 'apercu' && nRegul > 0 && <span className="bdg bad">{nRegul}</span>}
                </button>
              ))}
            </div>
            {view === 'calendrier' && (
              <>
                <div className="cg-monthnav">
                  <button className="gm-mn-arrow" onClick={() => setParams({ month: shiftYm(month, -1) })}>‹</button>
                  <span className="cg-mn-lbl">{ymLabel(month)}</span>
                  <button className="gm-mn-arrow" onClick={() => setParams({ month: shiftYm(month, 1) })}>›</button>
                  <button className="cg-mn-today" onClick={() => setParams({ month: null })}>Aujourd'hui</button>
                </div>
                <label className="cg-switch" title="Maladie, absences justifiées et injustifiées — utiles pour juger la couverture d'une journée">
                  <input type="checkbox" checked={showAbsences} onChange={(e) => setShowAbsences(e.target.checked)} data-testid="cg-show-absences" />
                  <span>Afficher aussi les absences</span>
                </label>
              </>
            )}
            <div className="cg-spacer" />
            <div className="cg-searchtop">
              <span>⌕</span>
              <input placeholder="Rechercher un employé…" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <button className="gm-attn-go" onClick={() => setSaisie({})}>+ Saisir un congé</button>
          </div>

          <div className="cg-rule">
            <span className="cg-rule-ic">i</span>
            <span>
              <b>22 jours ouvrables / an</b> · +1,83 j crédité le 1<sup>er</sup> de chaque mois · vendredi,
              samedi et jours fériés exclus du décompte · report du solde au 1<sup>er</sup> janvier.
            </span>
          </div>

          <div className="cg-kpis">
            {KPIS.map((k, i) => (
              <div key={i} className="cg-kpi" style={k.go ? { cursor: 'pointer' } : undefined} onClick={k.go ? () => go(k.go!) : undefined}>
                <span className={'cg-kpi-v ' + (k.cls ?? '')}>
                  <CgCount value={k.v} dec={k.dec === 0 ? 0 : 1} suffix={k.s} />
                </span>
                <span className="cg-kpi-l">{k.l}</span>
              </div>
            ))}
          </div>

          {view === 'apercu' && (
            <OverviewView data={overview.data} loading={overview.isLoading}
              onOpen={setDrawerEmp} onGoTo={go} onNew={() => setSaisie({})} />
          )}
          {view === 'soldes' && (
            conges.isLoading
              ? <div className="cg-placeholder view-enter">Chargement des soldes…</div>
              : <SoldesTable rows={rows} recupMap={recupMap} pendingByEmp={pendingByEmp}
                  onOpen={setDrawerEmp} onNew={(empId) => setSaisie({ empId })} />
          )}
          {view === 'calendrier' && (
            <LeaveCalendar entityId={entityId} month={month}
              types={showAbsences ? [...CAL_LEAVE_TYPES, ...CAL_ABSENCE_TYPES] : CAL_LEAVE_TYPES} q={q}
              balMap={calBalMap} onOpen={setDrawerEmp} onNew={(empId, date) => setSaisie({ empId, date })} />
          )}
          {view === 'demandes' && (
            <DemandesView pending={pending.data ?? []} balances={balMap} recupBalances={recupMap} onOpen={setDrawerEmp} />
          )}
        </div>
      </div>

      {drawerEmp && (
        <EmployeeDrawer employeeId={drawerEmp} page="conges" onClose={() => setDrawerEmp(null)}
          onNew={(empId) => { setDrawerEmp(null); setSaisie({ empId }); }}
          onCredit={(empId) => { setDrawerEmp(null); setCredit({ empId }); }} />
      )}
      {saisie && <SaisieModal type="CONGE" entityId={entityId} preset={saisie} onClose={() => setSaisie(null)} />}
      {credit && <CreditRecupModal preset={credit} onClose={() => setCredit(null)} />}
    </div>
  );
}

// ─── Soldes équipe table ─────────────────────────────────────
type SortKey = 'nom' | 'stock' | 'acquis' | 'consomme' | 'solde' | 'recup';

function SoldesTable({ rows, recupMap, pendingByEmp, onOpen, onNew }: {
  rows: LeaveBalanceRow[];
  recupMap: Map<string, LeaveBalanceRow>;
  pendingByEmp: Map<string, number>;
  onOpen: (empId: string) => void;
  onNew: (empId: string) => void;
}) {
  const [sort, setSort] = useState<{ k: SortKey; dir: 1 | -1 }>({ k: 'solde', dir: -1 });

  const sorted = useMemo(() => {
    const get: Record<SortKey, (r: LeaveBalanceRow) => string | number> = {
      nom: (r) => r.employee.name,
      stock: (r) => r.balance.initialStock,
      acquis: (r) => r.balance.accrued + r.balance.manualAdded,
      consomme: (r) => r.balance.consumed,
      solde: (r) => r.balance.current,
      recup: (r) => recupMap.get(r.employee.id)?.balance.current ?? 0,
    };
    const g = get[sort.k];
    return [...rows].sort((a, b) => {
      const va = g(a), vb = g(b);
      return (va < vb ? -1 : va > vb ? 1 : 0) * sort.dir;
    });
  }, [rows, sort, recupMap]);

  const T = sorted.reduce(
    (a, r) => ({
      stock: a.stock + r.balance.initialStock,
      acquis: a.acquis + r.balance.accrued + r.balance.manualAdded,
      consomme: a.consomme + r.balance.consumed,
      solde: a.solde + r.balance.current,
      recup: a.recup + (recupMap.get(r.employee.id)?.balance.current ?? 0),
    }),
    { stock: 0, acquis: 0, consomme: 0, solde: 0, recup: 0 },
  );

  const th = (k: SortKey, lbl: string, cls?: string) => (
    <th className={cls} onClick={() => setSort((s) => ({ k, dir: s.k === k ? (-s.dir as 1 | -1) : -1 }))}>
      <span className="cg-th">{lbl}<i className={'cg-caret' + (sort.k === k ? ' on' : '')}>{sort.k === k && sort.dir === 1 ? '▲' : '▼'}</i></span>
    </th>
  );

  return (
    <div className="gm-gridwrap view-enter">
      <div className="gm-scroll">
        <table className="sb-tbl">
          <thead>
            <tr>
              {th('nom', 'Employé', 'sb-cEmp')}
              {th('stock', 'Stock 1ᵉʳ janv.', 'num')}
              {th('acquis', '+ Acquis', 'num')}
              {th('consomme', '− Consommé', 'num')}
              {th('solde', '= Solde congés', 'num')}
              {th('recup', 'Stock récup', 'num')}
              <th>Consommation</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => {
              const b = r.balance;
              const acquis = b.accrued + b.manualAdded;
              const total = b.initialStock + acquis;
              const pct = total > 0 ? Math.max(0, Math.min(100, (b.consumed / total) * 100)) : 0;
              const recupCur = recupMap.get(r.employee.id)?.balance.current ?? 0;
              const pend = pendingByEmp.get(r.employee.id) ?? 0;
              const low = b.current < 1;
              return (
                <tr key={r.employee.id} className="cg-row" style={{ animationDelay: `${i * 18}ms` }} onClick={() => onOpen(r.employee.id)}>
                  <td className="sb-cEmp"><EmpCell emp={r.employee} /></td>
                  <td className="num gm-mono dim">{cgNum(b.initialStock)}</td>
                  <td className="num gm-mono up">+{cgNum(acquis)}</td>
                  <td className="num gm-mono down">{b.consumed ? `−${cgNum(b.consumed)}` : <span className="dim">0,0</span>}</td>
                  <td className="num">
                    <span className={'cg-solde gm-mono' + (low ? ' low' : '')}>{cgNum(b.current)}</span>
                    {pend > 0 && <span className="cg-pend" title={`${cgNum(pend)} j en attente`}>−{cgNum(pend)}</span>}
                  </td>
                  <td className="num"><span className={'cg-recupchip gm-mono' + (recupCur <= 0 ? ' zero' : '')}>{cgNum(recupCur)}</span></td>
                  <td style={{ width: 150 }}>
                    <div className="cg-gauge" title={`${cgNum(b.consumed)} j consommés sur ${cgNum(total)} j crédités`}>
                      <span className="cg-gauge-fill" style={{ width: `${pct}%` }} />
                    </div>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span className="cg-rowact">
                      <button className="cg-iconbtn" title="Saisir un congé" onClick={(ev) => { ev.stopPropagation(); onNew(r.employee.id); }}>+</button>
                      <button className="cg-iconbtn go" title="Détail">›</button>
                    </span>
                  </td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr><td colSpan={8}><div className="cg-empty" style={{ margin: 14 }}>Aucun employé pour ce filtre.</div></td></tr>
            )}
          </tbody>
          <tfoot>
            <tr className="sb-foot">
              <td className="gm-foot-lbl">{sorted.length} employés</td>
              <td className="num gm-mono">{cgNum(T.stock)}</td>
              <td className="num gm-mono up">+{cgNum(T.acquis)}</td>
              <td className="num gm-mono down">−{cgNum(T.consomme)}</td>
              <td className="num gm-mono strong">{cgNum(T.solde)}</td>
              <td className="num gm-mono strong">{cgNum(T.recup)}</td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
